export type GuestSavedSpot = {
  spotId: string;
  name: string;
  waterbody: string;
  location: string;
  savedAt: string;
};

export type GuestSavedSearch = {
  id: string;
  label: string;
  filters: Record<string, string | number | boolean>;
  createdAt: string;
};

export type GuestRecentSpot = Omit<GuestSavedSpot, "savedAt"> & {
  viewedAt: string;
};

export type GuestStorageSnapshot = {
  savedSpots: GuestSavedSpot[];
  savedSearches: GuestSavedSearch[];
  recentSpots: GuestRecentSpot[];
};

export type GuestStorageMutationResult = {
  snapshot: GuestStorageSnapshot;
  persisted: boolean;
};

const STORAGE_KEY = "dahlini.guest.v1";
const GUEST_SESSION_KEY = "dahlini.guest-session.v1";
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const emptySnapshot: GuestStorageSnapshot = {
  savedSpots: [],
  savedSearches: [],
  recentSpots: []
};

export function getGuestStorageSnapshot(): GuestStorageSnapshot {
  const storage = getStorage();

  if (!storage) {
    return emptySnapshot;
  }

  const rawValue = storage.getItem(STORAGE_KEY);

  if (!rawValue) {
    return emptySnapshot;
  }

  try {
    return normalizeSnapshot(JSON.parse(rawValue) as Partial<GuestStorageSnapshot>);
  } catch {
    return emptySnapshot;
  }
}

export function getGuestSessionId() {
  const storage = getStorage();
  if (!storage) {
    return null;
  }

  const existingId = storage.getItem(GUEST_SESSION_KEY);
  if (existingId && uuidPattern.test(existingId)) {
    return existingId;
  }

  const nextId = createLocalId();
  if (!uuidPattern.test(nextId)) {
    return null;
  }

  try {
    storage.setItem(GUEST_SESSION_KEY, nextId);
    return nextId;
  } catch {
    return null;
  }
}

export function saveGuestSpot(spot: Omit<GuestSavedSpot, "savedAt">) {
  const snapshot = getGuestStorageSnapshot();
  const nextSnapshot: GuestStorageSnapshot = {
    ...snapshot,
    savedSpots: [
      { ...spot, savedAt: new Date().toISOString() },
      ...snapshot.savedSpots.filter((savedSpot) => savedSpot.spotId !== spot.spotId)
    ].slice(0, 100)
  };

  return mutationResult(nextSnapshot);
}

export function removeGuestSpot(spotId: string) {
  const snapshot = getGuestStorageSnapshot();
  const nextSnapshot: GuestStorageSnapshot = {
    ...snapshot,
    savedSpots: snapshot.savedSpots.filter((savedSpot) => savedSpot.spotId !== spotId)
  };

  return mutationResult(nextSnapshot);
}

export function saveGuestSearch(search: Omit<GuestSavedSearch, "id" | "createdAt">) {
  const snapshot = getGuestStorageSnapshot();
  const nextSnapshot: GuestStorageSnapshot = {
    ...snapshot,
    savedSearches: [
      {
        ...search,
        id: createLocalId(),
        createdAt: new Date().toISOString()
      },
      ...snapshot.savedSearches.filter(
        (savedSearch) => JSON.stringify(savedSearch.filters) !== JSON.stringify(search.filters)
      )
    ].slice(0, 30)
  };

  return mutationResult(nextSnapshot);
}

export function removeGuestSearch(searchId: string) {
  const snapshot = getGuestStorageSnapshot();
  const nextSnapshot: GuestStorageSnapshot = {
    ...snapshot,
    savedSearches: snapshot.savedSearches.filter((search) => search.id !== searchId)
  };

  return mutationResult(nextSnapshot);
}

export function removeImportedGuestLibraryItems({
  spotIds,
  searchIds
}: {
  spotIds: string[];
  searchIds: string[];
}) {
  const snapshot = getGuestStorageSnapshot();
  const importedSpotIds = new Set(spotIds);
  const importedSearchIds = new Set(searchIds);
  const nextSnapshot: GuestStorageSnapshot = {
    ...snapshot,
    savedSpots: snapshot.savedSpots.filter((spot) => !importedSpotIds.has(spot.spotId)),
    savedSearches: snapshot.savedSearches.filter((search) => !importedSearchIds.has(search.id))
  };

  return mutationResult(nextSnapshot);
}

export function recordGuestRecentSpot(spot: Omit<GuestRecentSpot, "viewedAt">) {
  const snapshot = getGuestStorageSnapshot();
  const nextSnapshot: GuestStorageSnapshot = {
    ...snapshot,
    recentSpots: [
      { ...spot, viewedAt: new Date().toISOString() },
      ...snapshot.recentSpots.filter((recentSpot) => recentSpot.spotId !== spot.spotId)
    ].slice(0, 12)
  };

  return mutationResult(nextSnapshot);
}

function mutationResult(snapshot: GuestStorageSnapshot): GuestStorageMutationResult {
  return {
    snapshot,
    persisted: persistSnapshot(snapshot)
  };
}

function persistSnapshot(snapshot: GuestStorageSnapshot) {
  const storage = getStorage();

  if (!storage) {
    return false;
  }

  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    return true;
  } catch {
    return false;
  }
}

function normalizeSnapshot(snapshot: Partial<GuestStorageSnapshot>): GuestStorageSnapshot {
  return {
    savedSpots: Array.isArray(snapshot.savedSpots) ? snapshot.savedSpots.filter(isSavedSpot) : [],
    savedSearches: Array.isArray(snapshot.savedSearches) ? snapshot.savedSearches.filter(isSavedSearch) : [],
    recentSpots: Array.isArray(snapshot.recentSpots) ? snapshot.recentSpots.filter(isRecentSpot) : []
  };
}

function isSavedSpot(value: unknown): value is GuestSavedSpot {
  if (!value || typeof value !== "object") {
    return false;
  }

  const spot = value as Partial<GuestSavedSpot>;
  return Boolean(spot.spotId && spot.name && spot.waterbody && spot.location && spot.savedAt);
}

function isSavedSearch(value: unknown): value is GuestSavedSearch {
  if (!value || typeof value !== "object") {
    return false;
  }

  const search = value as Partial<GuestSavedSearch>;
  return Boolean(search.id && search.label && search.filters && search.createdAt);
}

function isRecentSpot(value: unknown): value is GuestRecentSpot {
  if (!value || typeof value !== "object") {
    return false;
  }

  const spot = value as Partial<GuestRecentSpot>;
  return Boolean(spot.spotId && spot.name && spot.waterbody && spot.location && spot.viewedAt);
}

function getStorage() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function createLocalId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `local-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
